import {createIntl, createIntlCache} from "react-intl";

export {defineMessage, defineMessages} from "react-intl";

import {validationMessages} from "./messages/shared.messages";

export const SOURCE_LOCALE = "en-GB";
export const DEFAULT_LOCALE = SOURCE_LOCALE;
export const SUPPORTED_LOCALES = [SOURCE_LOCALE] as const;

const RTL_LANGUAGE_PREFIXES = ["ar", "fa", "he", "ps", "ur"] as const;
const intlCache = createIntlCache();

export type LocaleCode = string;
export type Direction = "ltr" | "rtl";
export type MessageId = string;
export type FieldLabelId = string;
export type ValidationMessageId = string;
export type TranslationPrimitive = boolean | number | string | null;
export type TranslationValues = Record<string, TranslationPrimitive | undefined>;

export interface I18nRuntime {
  locale: LocaleCode;
  dir: Direction;
  intl: ReturnType<typeof createIntl>;
}

export interface ProjectMessageDescriptor {
  id: MessageId;
  values?: TranslationValues;
}

export interface ValidationDescriptor {
  id: ValidationMessageId;
  values?: TranslationValues & {
    fieldLabelId?: FieldLabelId;
  };
}

export interface CreateI18nRuntimeInput {
  defaultLocale?: LocaleCode;
  locale: LocaleCode;
  messages: Record<string, string>;
}

export interface ResolveSupportedLocaleOptions {
  onUnsupported?: "fallback" | "throw";
}

export function resolveSupportedLocale(
  locale: LocaleCode,
  options: ResolveSupportedLocaleOptions = {},
): LocaleCode {
  if (SUPPORTED_LOCALES.includes(locale as (typeof SUPPORTED_LOCALES)[number])) {
    return locale;
  }

  if (options.onUnsupported === "throw") {
    throw new Error(`Unsupported locale: ${locale}`);
  }

  return DEFAULT_LOCALE;
}

export function getDirection(locale: LocaleCode): Direction {
  const language = locale.toLowerCase().split("-")[0];

  return RTL_LANGUAGE_PREFIXES.includes(language as (typeof RTL_LANGUAGE_PREFIXES)[number])
    ? "rtl"
    : "ltr";
}

export function createI18nRuntime({
  defaultLocale = DEFAULT_LOCALE,
  locale,
  messages,
}: CreateI18nRuntimeInput): I18nRuntime {
  const canonicalLocale = resolveSupportedLocale(locale);

  return {
    locale: canonicalLocale,
    dir: getDirection(canonicalLocale),
    intl: createIntl({defaultLocale, locale: canonicalLocale, messages}, intlCache),
  };
}

export function messagesToCatalog(
  descriptors: Record<string, {defaultMessage?: string | undefined; id: string}>,
): Record<string, string> {
  return Object.fromEntries(
    Object.values(descriptors).map((descriptor) => [
      descriptor.id,
      descriptor.defaultMessage ?? "",
    ]),
  );
}

export function createValidationDescriptor(
  id: ValidationMessageId,
  values?: ValidationDescriptor["values"],
): ValidationDescriptor {
  return values ? {id, values} : {id};
}

export function requiredFieldValidation(fieldLabelId: FieldLabelId): ValidationDescriptor {
  return createValidationDescriptor(validationMessages.required.id, {fieldLabelId});
}

export function invalidEmailValidation(fieldLabelId: FieldLabelId): ValidationDescriptor {
  return createValidationDescriptor(validationMessages.invalidEmail.id, {fieldLabelId});
}

export function createValidationIssue(descriptor: ValidationDescriptor) {
  return {
    code: "custom" as const,
    message: descriptor.id,
    params: {descriptor},
  };
}

export function isValidationDescriptor(value: unknown): value is ValidationDescriptor {
  return !!value && typeof value === "object" && "id" in value && typeof value.id === "string";
}

export function extractValidationDescriptor(value: unknown): ValidationDescriptor | null {
  if (isValidationDescriptor(value)) return value;
  if (!value || typeof value !== "object") return null;

  const params = "params" in value ? (value as {params?: unknown}).params : undefined;

  if (!params || typeof params !== "object" || !("descriptor" in params)) {
    return null;
  }

  const descriptor = (params as {descriptor?: unknown}).descriptor;

  return isValidationDescriptor(descriptor) ? descriptor : null;
}

export function translateMessage(
  runtime: I18nRuntime,
  descriptor: ProjectMessageDescriptor,
): string {
  return runtime.intl.formatMessage({id: descriptor.id}, descriptor.values);
}

export function translateValidationDescriptor(
  runtime: I18nRuntime,
  descriptor: ValidationDescriptor,
): string {
  const values = {...descriptor.values};
  const fieldLabelId = values?.fieldLabelId;

  if (fieldLabelId) {
    const fieldLabel = runtime.intl.formatMessage({id: fieldLabelId});

    delete values.fieldLabelId;

    return runtime.intl.formatMessage({id: descriptor.id}, {...values, fieldLabel});
  }

  return runtime.intl.formatMessage({id: descriptor.id}, values);
}

export {commonMessages, validationMessages} from "./messages/shared.messages";
